/**
 * Turning an API rejection into something a visitor can act on.
 *
 * The API documents that production may replace error message texts with a generic one, so nothing
 * here keys off `error.message` — only the HTTP status and `error.code`.
 */

export type FailureKind =
	| 'busy'
	| 'disabled'
	| 'rate-limited'
	| 'bad-session-id'
	| 'session-gone'
	| 'server'
	| 'network'

export interface ApiFailure {
	kind: FailureKind
	/** True when a manual retry is worth offering. Never used to retry automatically. */
	retryable: boolean
	/** True when the cause is on the platform side, so the page must not blame the visitor. */
	ourFault: boolean
	/** Only for rate limiting. */
	retryAfterMs?: number
}

const DEFAULT_BACKOFF_MS = 5000

/** Reads `error.code` out of the documented envelope, tolerating a non-JSON error page. */
export function parseErrorEnvelope(body: unknown): string | null {
	if (typeof body !== 'object' || body === null) return null

	const error = (body as { error?: unknown }).error
	if (typeof error !== 'object' || error === null) return null

	const code = (error as { code?: unknown }).code
	return typeof code === 'string' && code.length > 0 ? code : null
}

export function classifyFailure(status: number, code: string | null, retryAfter?: string): ApiFailure {
	// The code outranks the status: it is the part of the contract that is promised to be stable.
	if (code === 'call_test_disabled') return { kind: 'disabled', retryable: true, ourFault: true }
	if (code === 'conflict') return { kind: 'busy', retryable: true, ourFault: true }
	if (code === 'id') return { kind: 'bad-session-id', retryable: false, ourFault: true }
	if (code === 'session') return { kind: 'session-gone', retryable: true, ourFault: false }

	if (status === 0) return { kind: 'network', retryable: true, ourFault: false }
	if (status === 409) return { kind: 'busy', retryable: true, ourFault: true }
	if (status === 422) return { kind: 'bad-session-id', retryable: false, ourFault: true }
	if (status === 404) return { kind: 'session-gone', retryable: true, ourFault: false }
	if (status === 429) {
		return {
			kind: 'rate-limited',
			retryable: true,
			ourFault: false,
			retryAfterMs: parseRetryAfter(retryAfter)
		}
	}

	// Anything else is ours to own if the server produced it, and merely unexpected if it did not.
	return { kind: 'server', retryable: true, ourFault: status >= 500 }
}

/** Only the delta-seconds form is used; an HTTP-date is ignored in favour of a fixed backoff. */
function parseRetryAfter(retryAfter?: string): number {
	const seconds = Number(retryAfter)
	if (!Number.isFinite(seconds) || seconds <= 0) return DEFAULT_BACKOFF_MS
	return Math.round(seconds * 1000)
}
